import { baseApi } from '@/lib/store/api/baseQuery'

/**
 * Single FE type for GET /api/external/urgent_care/orders/{order_id}/locations (GM-6964).
 *
 * Shape mirrors FulfillmentOwnerOrchestrators::Types::AppointmentAvailability as JSON,
 * with `participant_id` / `location_id` elevated for FE selection (Solv id; same as
 * participant.id). When pricing is unavailable the backend omits `pricing` or returns
 * null — do not invent amounts. Booking query-string keys live in `locationsContract`.
 */
export interface UrgentCareLocationAddress {
    line_1: string
    line_2?: string | null
    city: string
    state: string
    zip: string
}

export interface UrgentCareLocationSlot {
    /** ISO8601 */
    start_time: string
    /** ISO8601 */
    end_time: string
}

export interface UrgentCareLocationPricing {
    amount: number
    /** Typically `cents` from SolvMapper::PRICE_UNITS */
    unit: string
}

export interface UrgentCareLocationInsurance {
    medicaid?: boolean | null
    medicare?: boolean | null
    private_insurance?: 'in_network' | 'out_of_network' | 'unknown' | null
    workers_comp?: boolean | null
    self_pay?: boolean | null
}

export interface UrgentCareLocationParticipant {
    id: string
    name?: string | null
}

export type UrgentCareWeekday =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'

/** Clock times as `HH:MM:SS` (or ISO) for a single day window. */
export interface UrgentCareDailyHours {
    start_time?: string | null
    end_time?: string | null
}

export type UrgentCareLocationHours = Partial<Record<UrgentCareWeekday, UrgentCareDailyHours | null>>

export interface UrgentCareLocation {
    /** Solv location id when elevated by Core (preferred selection key). */
    location_id?: string | null
    /** Solv participant / location id from AppointmentAvailability serializers. */
    participant_id?: string | null
    name: string
    address: UrgentCareLocationAddress
    encounter_class?: 'in_person' | 'virtual' | null
    available_slots: UrgentCareLocationSlot[]
    /** First few bookable slots for list-card chips (Core also returns full `available_slots`). */
    preview_slots?: UrgentCareLocationSlot[] | null
    distance_miles?: number | null
    rating?: number | null
    rating_count?: number | null
    hours?: UrgentCareLocationHours | null
    /** IANA timezone for open/closed labels when present. */
    time_zone?: string | null
    pediatric_care_status?: string | null
    pricing?: UrgentCareLocationPricing | null
    insurance?: UrgentCareLocationInsurance | null
    participant?: UrgentCareLocationParticipant | null
    /** True when Core joined a walk-in availability row onto this location. */
    walk_in_supported?: boolean | null
    /** Estimated walk-in wait from Solv, e.g. `15-30 min` or `10 min`. */
    wait_time?: string | null
    /** Current walk-in queue length from Solv when walk-in is offered. */
    waitlist_length?: number | null
}

export interface UrgentCareLocationsResponse {
    locations: UrgentCareLocation[]
}

export type GetUrgentCareOrderLocationsArg = {
    orderId: string
    /** Optional ZIP override for geo-scoped search (`zip` query param). */
    zip?: string
    /**
     * Inclusive availability window start (ISO8601). Paired with `endTime` for
     * "appointments today" and other day-bounded searches.
     */
    startTime?: string
    /** Inclusive availability window end (ISO8601). */
    endTime?: string
    /** Minimum star rating (e.g. 4 for "4+ stars"). */
    minimumRating?: number
    /** When true, Core returns only in-network locations. */
    inNetwork?: boolean
    /**
     * Solv location / FO participant id. When set, Core filters availability to that
     * participant (`participant_id` query param).
     */
    participantId?: string
}

/** Builds Core query params, omitting unset filters. */
export function buildUrgentCareLocationsQueryParams(
    arg: Omit<GetUrgentCareOrderLocationsArg, 'orderId'>,
): Record<string, string> | undefined {
    const params: Record<string, string> = {}
    if (arg.zip) params.zip = arg.zip
    if (arg.startTime) params.start_time = arg.startTime
    if (arg.endTime) params.end_time = arg.endTime
    if (arg.minimumRating != null) params.minimum_rating = String(arg.minimumRating)
    if (arg.inNetwork != null) params.in_network = String(arg.inNetwork)
    if (arg.participantId) params.participant_id = arg.participantId
    return Object.keys(params).length > 0 ? params : undefined
}

export const urgentCareLocationsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getUrgentCareOrderLocations: builder.query<
            UrgentCareLocationsResponse,
            GetUrgentCareOrderLocationsArg
        >({
            query: ({ orderId, ...filters }) => ({
                url: `/api/external/urgent_care/orders/${orderId}/locations`,
                params: buildUrgentCareLocationsQueryParams(filters),
            }),
            providesTags: (_result, _error, { orderId, participantId }) => [
                {
                    type: 'UrgentCareOrderLocations',
                    id: participantId ? `${orderId}:${participantId}` : orderId,
                },
            ],
        }),
    }),
    overrideExisting: false,
})

export const { useGetUrgentCareOrderLocationsQuery } = urgentCareLocationsApi
